import { Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { ViewIcon } from 'lucide-react';

import { AirIdDisplay } from '@/components/air-id-display';
import { ArrowTooltip } from '@/components/arrow-tooltip';
import { CopyButton } from '@/components/copy-button';
import { CopyableLink } from '@/components/copyable-link';
import { LinkToExternal } from '@/components/link-to-external';
import { MeetingStatusBadge } from '@/components/session/meeting-status-badge';
import { SessionFeedbackStatus } from '@/components/session/session-feedback-status';
import { createTableRowSelectionCell, PinnedComponent } from '@/components/table/entity-table';
import { Button } from '@/components/ui/button';
import { getCoachAttendanceGradeLabel } from '@/lib/coach-utils';
import { getDistrictColumn } from '@/lib/district-utils';
import { PeopleBadgeList } from '@/routes/_layout/scheduling/student-groups/-components/people-badge-list';
import { SessionTeacherDisplay } from '@/routes/_layout/sessions/-components/session-teacher-display';
import {
  distinct,
  formatClassTimeMeetingTimePairLabel,
  formatDateWithDayOfWeek,
  formatInstant,
  formatTimeRange,
  FrequentTimeZones,
  getSessionSubstitutionRequestSummary,
  isNotNullish,
  isZoomMeetingNotStarted,
  safeInstantFromString,
} from '@airreading/common';
import {
  AdminSession,
  OriginalTeacherIssueLabelMap,
  StudentYearColorMap,
  studentYearOption,
  VideoConferenceTypeLabels,
} from '@airreading/common/data';
import { UserPenIcon } from 'lucide-react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { SubSessionDrawer } from '../sub-sessions/-components/SubSessionDrawer';
import { CheckboxGoogleMeetChecked } from './checkbox-google-meet-checked';
import { CheckboxPinnedSubSession } from './checkbox-pinned-sub-session';
import { CheckboxUpdateSupportAdmin } from './checkbox-update-support-admin';
import { EditSessionModal } from './edit-session-modal';
import { SessionTeacherWithEditTrigger } from './edit-session-teacher-modal';
import { OriginalTeacherDisplay } from './original-teacher-display';
import { SessionCompletionStatusCell } from './session-completion-status-cell';
import { SessionLink } from './session-link';
import { TeacherDisplayTagBadgeV2 } from './teacher-display-tag-badge-v2';

const adminCol = createColumnHelper<AdminSession>();

function CoachRemarksV2Header() {
  return (
    <div className="flex items-center gap-2">
      <span className="font-semibold">Session remarks</span>
    </div>
  );
}

export const sessionPageColumnsEnum = z.enum([
  'sessionId',
  'editSession',
  'date',
  'time',
  'status',
  'meetingStatus',
  'meetingStartInstant',
  'supportAdmin',
  'joinedStudentsCount',
  'sessionTeacher',
  'originalTeacher',
  'teacherAttendanceGrade',
  'videoConferenceType',
  'zoomPersonalMeetingId',
  'zoomLink',
  'zoomHostUrl',
  'googleMeetLink',
  'googleMeetChecked',
  'lessonLink',
  'district',
  'school',
  'grades',
  'groupAttendance',
  'students',
  'numberOfStudents',
  'lessonName',
  'originalTeacherIssue',
  'coachDisplayTags',
  'sessionCompletionStatus',
  'canceledAtInstant',
  'substituteTimestamp',
  'recordingLink',
  'coachSessionRemarksV2',
  'classId',
  'sessionAirId',
  'subSessionDrawer',
  'lastManualUpdatedAt',
]);

export const SessionPageColumns = sessionPageColumnsEnum.enum;
export type SessionPageColumns = z.infer<typeof sessionPageColumnsEnum>;

function adminTableRowTestId(tablePrefix: string | undefined, element: string, rowNumber: number): string | undefined {
  return tablePrefix ? `${tablePrefix}-table-${element}-${rowNumber}` : undefined;
}

function adminTableCellTestId(tablePrefix: string | undefined, field: string, rowNumber: number): string | undefined {
  return tablePrefix ? `${tablePrefix}-table-cell-${field}-${rowNumber}` : undefined;
}

function sessionAirIdTableColumn(getCellTestId?: (rowNumber: number) => string | undefined) {
  return adminCol.accessor('id', {
    id: SessionPageColumns.sessionAirId,
    header: 'Session ID',
    cell: ({ getValue, row }) => {
      const rowNumber = row.index + 1;
      return (
        <AirIdDisplay
          entityType="ses"
          rawId={getValue()}
          wrapper={(displayId) => (
            <span className="text-black" data-testid={getCellTestId?.(rowNumber)}>
              {displayId}
            </span>
          )}
        />
      );
    },
  });
}

export const sessionsPageColumns = (displayTimeZone: string, from?: string) => [
  createTableRowSelectionCell(adminCol),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.editSession,
    header: '',
    cell: ({ getValue }) => <EditSessionModal session={getValue()} />,
    size: 50,
  }),

  adminCol.accessor('id', {
    id: SessionPageColumns.sessionId,
    header: '',
    cell: ({ getValue }) => {
      const searchProps = from ? { search: { from } } : {};
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: getValue() }} {...searchProps}>
          <ViewIcon size={16} />
        </Link>
      );
    },
  }),

  ...(from === 'school'
    ? [
        adminCol.accessor('studentGroupId', {
          id: SessionPageColumns.classId,
          header: 'Class ID',
          cell: ({ getValue }) => {
            const studentGroupId = getValue();
            return (
              <AirIdDisplay
                entityType="cls"
                rawId={studentGroupId}
                wrapper={(displayId) => <span className="text-black">{displayId}</span>}
              />
            );
          },
        }),
      ]
    : []),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.date,
    header: 'Date',
    enableSorting: true,
    cell: ({ getValue }) => {
      const startInstant = getValue();
      return formatDateWithDayOfWeek(startInstant, displayTimeZone);
    },
  }),

  adminCol.accessor('startPlainTime', {
    id: SessionPageColumns.time,
    header: 'Time (CT)',
    enableSorting: true,
    cell: ({ getValue, row }) => {
      const startPlainTime = getValue();
      const endPlainTime = row.original.endPlainTime;
      if (!startPlainTime || !endPlainTime) {
        return '—';
      }

      return formatClassTimeMeetingTimePairLabel(
        Temporal.PlainTime.from(startPlainTime),
        Temporal.PlainTime.from(endPlainTime),
      );
    },
  }),

  adminCol.accessor('meeting.status', {
    id: SessionPageColumns.meetingStatus,
    header: 'Meeting status',
    cell: ({ getValue, row }) => {
      return <MeetingStatusBadge status={getValue()} zoomNotStarted={isZoomMeetingNotStarted(row.original.meeting)} />;
    },
  }),

  adminCol.accessor('meeting.startInstant', {
    id: SessionPageColumns.meetingStartInstant,
    header: 'Meeting started timestamp',
    cell: ({ getValue }) => {
      const startedAt = getValue();
      if (!startedAt) {
        return 'Not started';
      }

      const { formattedTime } = formatInstant(startedAt, displayTimeZone, {
        hour12: true,
      });
      return formattedTime.toLowerCase();
    },
  }),

  adminCol.accessor('joinedStudentsCount', {
    id: SessionPageColumns.joinedStudentsCount,
    header: '# of students joined',
    cell: ({ getValue }) => getValue(),
  }),

  adminCol.accessor('students', {
    id: 'students',
    header: 'Students',
    cell: ({ getValue }) => {
      return <PeopleBadgeList people={getValue()} />;
    },
  }),

  adminCol.accessor((row) => row.students?.length ?? 0, {
    id: SessionPageColumns.numberOfStudents,
    header: '# of students',
    cell: ({ getValue }) => getValue(),
  }),

  adminCol.accessor('gradeByStudentId', {
    id: 'grades',
    header: 'Grades',
    cell: ({ getValue }) => {
      const grades = Object.values(getValue()).filter(distinct()).filter(isNotNullish);
      if (!grades || grades.length === 0) return <div>-</div>;

      return (
        <div className="flex gap-1 flex-wrap">
          {grades.map((grade, index) => {
            if (!grade) return null;
            const backgroundColor = StudentYearColorMap[grade];
            return (
              <span
                key={index}
                className="text-xs rounded-full px-2 py-1 min-w-9 text-center"
                style={{ backgroundColor }}
              >
                {studentYearOption(grade)}
              </span>
            );
          })}
        </div>
      );
    },
  }),

  adminCol.accessor('school', {
    id: 'district',
    header: 'District',
    cell: ({ getValue }) => {
      const school = getValue();
      const districtName = school?.org?.name;
      if (!districtName) return <span>Unknown</span>;
      return getDistrictColumn({
        orgId: school.orgId,
        orgName: districtName,
      });
    },
  }),

  adminCol.accessor('school', {
    id: 'school',
    header: 'School',
    cell: ({ getValue }) => {
      const school = getValue();
      if (!school) return <span>Unknown</span>;
      return (
        <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
          {school.name}
        </Link>
      );
    },
  }),

  adminCol.accessor('lessonLink', {
    id: SessionPageColumns.lessonLink,
    header: 'Lesson',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const lesson = row.original.lesson;
      if (!link || !lesson) return '—';

      return (
        <div className="flex items-center gap-2">
          <Link to={'/lessons/$lessonId'} params={{ lessonId: lesson.id }} className="underline">
            {lesson.name}
          </Link>
          <CopyButton content={link} />
        </div>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionTeacher,
    header: 'Session teacher',
    cell: ({ getValue }) => {
      return <SessionTeacherDisplay session={getValue()} />;
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.originalTeacher,
    header: 'Original teacher',
    cell: ({ row }) => {
      return <OriginalTeacherDisplay session={row.original} />;
    },
  }),

  adminCol.accessor('coach', {
    id: SessionPageColumns.teacherAttendanceGrade,
    header: 'Attendance grade - Session teacher',
    cell: ({ getValue }) => {
      const coach = getValue();
      return getCoachAttendanceGradeLabel(coach?.coachProfile?.attendanceGrade);
    },
  }),

  adminCol.accessor('videoConferenceType', {
    id: SessionPageColumns.videoConferenceType,
    header: 'Meeting type',
    cell: ({ getValue }) => {
      const meetingType = getValue();
      if (!meetingType) return '—';
      return VideoConferenceTypeLabels[meetingType];
    },
  }),

  adminCol.accessor((row) => row.coach?.coachProfile?.zoomPersonalMeetingId, {
    id: SessionPageColumns.zoomPersonalMeetingId,
    header: 'Zoom ID',
    cell: ({ getValue }) => getValue() || '—',
  }),

  adminCol.accessor((row) => row.zoom?.attendeeUrl, {
    id: SessionPageColumns.zoomLink,
    header: 'Zoom link',
    cell: ({ getValue }) => {
      const link = getValue();
      return link ? <SessionLink href={link} /> : '—';
    },
  }),

  adminCol.accessor((row) => row.zoom?.hostUrl, {
    id: SessionPageColumns.zoomHostUrl,
    header: 'Zoom host URL',
    cell: ({ getValue }) => {
      const link = getValue();
      if (!link) return '—';

      return (
        <div className="flex items-center gap-2">
          <SessionLink href={link}>Open Zoom host link</SessionLink>
          <CopyButton content={link} />
        </div>
      );
    },
  }),

  adminCol.accessor((row) => row.googleMeet?.attendeeUrl, {
    id: SessionPageColumns.googleMeetLink,
    header: 'Google Meet URL',
    cell: ({ getValue }) => {
      const link = getValue();
      return link ? <SessionLink href={link} /> : '—';
    },
  }),

  adminCol.accessor('groupAverageAttendanceLast14Days', {
    id: SessionPageColumns.groupAttendance,
    header: 'Student average attendance percentage - last 14 days',
    cell: ({ getValue }) => {
      const value = getValue();
      return value !== null ? `${value}%` : 'N/A';
    },
  }),

  adminCol.accessor('originalTeacherIssue', {
    id: SessionPageColumns.originalTeacherIssue,
    header: 'Original teacher issue',
    cell: ({ getValue }) => {
      const originalTeacherIssue = getValue();
      if (!originalTeacherIssue) {
        return null;
      }
      return OriginalTeacherIssueLabelMap[originalTeacherIssue];
    },
  }),

  adminCol.accessor('coachSessionRemarksV2', {
    id: SessionPageColumns.coachSessionRemarksV2,
    header: () => <CoachRemarksV2Header />,
    cell: ({ getValue }) => <TeacherDisplayTagBadgeV2 tags={getValue()} />,
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionCompletionStatus,
    header: 'Teacher display - Class status',
    cell: ({ getValue }) => {
      const {
        sessionCompletionStatus,
        coachSessionRemarksV2,
        zoom,
        startInstant,
        endInstant,
        addedCoachSessionRemarksV2,
      } = getValue();
      return (
        <SessionCompletionStatusCell
          status={sessionCompletionStatus}
          coachSessionRemarksV2={coachSessionRemarksV2}
          session={{ zoom, startInstant, endInstant, addedCoachSessionRemarksV2 }}
        />
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: 'createdAt',
    header: 'Created time (CT)',
    enableSorting: true,
    cell: ({ getValue }) => {
      const { createdAt } = getValue();
      const instant = safeInstantFromString(String(createdAt));
      const { formattedDateTime } = formatInstant(instant, FrequentTimeZones.UsCentral);
      return <div>{formattedDateTime}</div>;
    },
  }),

  ...(from === 'school'
    ? [
        adminCol.accessor((row) => row, {
          id: SessionPageColumns.lastManualUpdatedAt,
          header: 'Last manual updated time (CT)',
          enableSorting: true,
          cell: ({ getValue }) => {
            const { lastManualUpdatedAt } = getValue();
            if (!lastManualUpdatedAt) {
              return <div>-</div>;
            }
            const instant = safeInstantFromString(String(lastManualUpdatedAt));
            const { formattedDateTime } = formatInstant(instant, FrequentTimeZones.UsCentral);
            return <div>{formattedDateTime}</div>;
          },
        }),
      ]
    : [
        adminCol.accessor((row) => row, {
          id: 'updatedAt',
          header: 'Last updated time (CT)',
          enableSorting: true,
          cell: ({ getValue }) => {
            const { updatedAt } = getValue();
            const instant = safeInstantFromString(String(updatedAt));
            const { formattedDateTime } = formatInstant(instant, FrequentTimeZones.UsCentral);
            return <div>{formattedDateTime}</div>;
          },
        }),
      ]),

  sessionAirIdTableColumn(),
];

export const futureSessionsPageColumns = (displayTimeZone: string, tablePrefix?: string) => [
  createTableRowSelectionCell(adminCol),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.editSession,
    header: '',
    cell: ({ getValue }) => <EditSessionModal session={getValue()} />,
    size: 50,
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.date,
    header: 'Date',
    cell: ({ getValue }) => {
      const session = getValue();
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: session.id }} search={{ from: 'future' }}>
          {formatDateWithDayOfWeek(session.startInstant, displayTimeZone)}
        </Link>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.time,
    header: 'Time (CT)',
    cell: ({ getValue, row }) => {
      const endInstant = row.original.endInstant;
      const session = getValue();
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: session.id }} search={{ from: 'future' }}>
          {formatTimeRange(session.startInstant, endInstant, FrequentTimeZones.UsCentral)}
        </Link>
      );
    },
  }),

  adminCol.accessor('meeting.status', {
    id: SessionPageColumns.meetingStatus,
    header: 'Meeting status',
    cell: ({ getValue, row }) => {
      return <MeetingStatusBadge status={getValue()} zoomNotStarted={isZoomMeetingNotStarted(row.original.meeting)} />;
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionTeacher,
    header: 'Teacher',
    cell: ({ getValue, row }) => {
      const session = getValue();
      const rowNumber = row.index + 1;
      return (
        <SessionTeacherWithEditTrigger
          session={session}
          teacherTestId={adminTableCellTestId(tablePrefix, 'teacher', rowNumber)}
          triggerTestId={adminTableRowTestId(tablePrefix, 'button-edit-teacher', rowNumber)}
        />
      );
    },
  }),

  adminCol.accessor('coach', {
    id: SessionPageColumns.teacherAttendanceGrade,
    header: 'Teacher attendance',
    cell: ({ getValue }) => {
      const coach = getValue();
      return getCoachAttendanceGradeLabel(coach?.coachProfile?.attendanceGrade);
    },
  }),

  adminCol.accessor('students', {
    id: 'students',
    header: 'Students',
    cell: ({ getValue }) => {
      return <PeopleBadgeList people={getValue()} />;
    },
  }),

  adminCol.accessor('gradeByStudentId', {
    id: 'grades',
    header: 'Grade',
    cell: ({ getValue }) => {
      const grades = Object.values(getValue()).filter(distinct()).filter(isNotNullish);
      if (!grades || grades.length === 0) return <div>-</div>;

      return (
        <div className="flex gap-1 flex-wrap">
          {grades.map((grade, index) => {
            if (!grade) return null;
            const backgroundColor = StudentYearColorMap[grade];
            return (
              <span
                key={index}
                className="text-xs rounded-full px-2 py-1 min-w-9 text-center"
                style={{ backgroundColor }}
              >
                {studentYearOption(grade)}
              </span>
            );
          })}
        </div>
      );
    },
  }),

  adminCol.accessor('school', {
    id: 'school-district',
    header: 'School - District',
    cell: ({ getValue }) => {
      const school = getValue();
      return (school?.name || 'Unknown') + ' - ' + (school?.org?.name || 'Unknown');
    },
  }),

  adminCol.accessor('videoConferenceType', {
    id: SessionPageColumns.videoConferenceType,
    header: 'Meeting type',
    cell: ({ getValue }) => {
      const meetingType = getValue();
      if (!meetingType) return '—';
      return VideoConferenceTypeLabels[meetingType];
    },
  }),

  adminCol.accessor((row) => row.zoom?.attendeeUrl, {
    id: SessionPageColumns.zoomLink,
    header: 'Zoom link',
    cell: ({ getValue }) => {
      const link = getValue();
      return link ? <SessionLink href={link} /> : '—';
    },
  }),

  adminCol.accessor((row) => row.googleMeet?.attendeeUrl, {
    id: SessionPageColumns.googleMeetLink,
    header: 'Google Meet URL',
    cell: ({ getValue }) => {
      const link = getValue();
      return link ? <SessionLink href={link} /> : '—';
    },
  }),

  adminCol.accessor('lessonLink', {
    id: SessionPageColumns.lessonLink,
    header: 'Lesson',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const lesson = row.original.lesson;
      if (!lesson) return '—';
      if (!link) return <div className="flex items-center gap-2">{lesson.name}</div>;

      return (
        <div className="flex items-center gap-2">
          <CopyableLink
            externalHref={link}
            className="text-blue-600 hover:text-blue-800 underline"
            showExternalHrefIcon={false}
          >
            {lesson.name}
          </CopyableLink>
        </div>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.originalTeacher,
    header: 'Original teacher',
    cell: ({ row }) => {
      return <OriginalTeacherDisplay session={row.original} />;
    },
  }),

  adminCol.accessor('coachSessionRemarksV2', {
    id: SessionPageColumns.coachSessionRemarksV2,
    header: () => <CoachRemarksV2Header />,
    cell: ({ getValue }) => <TeacherDisplayTagBadgeV2 tags={getValue()} />,
  }),

  adminCol.accessor('canceledAtInstant', {
    id: SessionPageColumns.canceledAtInstant,
    header: 'Cancelation timestamp',
    cell: ({ getValue }) => {
      const canceledAtInstant = getValue();

      if (!canceledAtInstant) return null;

      const { formattedTime, formattedDate } = formatInstant(canceledAtInstant, displayTimeZone, {
        hour12: true,
      });

      return <span className="lowercase">{`${formattedTime}, ${formattedDate}`}</span>;
    },
  }),

  sessionAirIdTableColumn(),
];

export const todaySessionsPageColumns = (displayTimeZone: string, from?: string, tablePrefix?: string) => [
  createTableRowSelectionCell(adminCol, {
    isPinned: true,
    selectAllTestId: tablePrefix ? `${tablePrefix}-table-checkbox-select-all` : undefined,
    rowSelectTestId: tablePrefix
      ? (rowNumber) => adminTableRowTestId(tablePrefix, 'checkbox-select', rowNumber)!
      : undefined,
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.editSession,
    header: '',
    cell: ({ getValue, column, row }) => {
      const session = getValue();
      const rowNumber = row.index + 1;
      return (
        <PinnedComponent pinned="left" column={column}>
          <EditSessionModal
            session={session}
            triggerTestId={adminTableRowTestId(tablePrefix, 'button-edit', rowNumber)}
          />
        </PinnedComponent>
      );
    },
    size: 60,
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.date,
    header: 'Date',
    cell: ({ getValue, column, row }) => {
      const searchProps = from ? { search: { from } } : {};
      const session = getValue();
      const rowNumber = row.index + 1;
      return (
        <PinnedComponent pinned="left" column={column}>
          <Link
            to="/sessions/$sessionId"
            params={{ sessionId: session.id }}
            {...searchProps}
            data-testid={adminTableRowTestId(tablePrefix, 'link-date', rowNumber)}
          >
            {formatDateWithDayOfWeek(session.startInstant, displayTimeZone)}
          </Link>
        </PinnedComponent>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.time,
    header: 'Time (CT)',
    cell: ({ getValue, column, row }) => {
      const searchProps = from ? { search: { from } } : {};
      const session = getValue();
      const rowNumber = row.index + 1;
      return (
        <PinnedComponent pinned="left" column={column}>
          <Link
            to="/sessions/$sessionId"
            params={{ sessionId: session.id }}
            {...searchProps}
            data-testid={adminTableRowTestId(tablePrefix, 'link-time', rowNumber)}
          >
            {formatTimeRange(session.startInstant, session.endInstant, FrequentTimeZones.UsCentral)}
          </Link>
        </PinnedComponent>
      );
    },
    minSize: 165,
  }),

  adminCol.accessor('meeting.status', {
    id: SessionPageColumns.meetingStatus,
    header: 'Meeting status',
    cell: ({ getValue, column, row }) => (
      <PinnedComponent pinned="left" column={column}>
        <MeetingStatusBadge
          status={getValue()}
          zoomNotStarted={isZoomMeetingNotStarted(row.original.meeting)}
          data-testid={adminTableCellTestId(tablePrefix, 'meeting-status', row.index + 1)}
          warningTestId={adminTableCellTestId(tablePrefix, 'zoom-not-started-warning', row.index + 1)}
        />
      </PinnedComponent>
    ),
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionTeacher,
    header: 'Teacher',
    cell: ({ getValue, column, row }) => {
      const session = getValue();
      const rowNumber = row.index + 1;
      return (
        <PinnedComponent pinned="left" column={column}>
          <SessionTeacherWithEditTrigger
            session={session}
            teacherTestId={adminTableCellTestId(tablePrefix, 'teacher', rowNumber)}
            triggerTestId={adminTableRowTestId(tablePrefix, 'button-edit-teacher', rowNumber)}
          />
        </PinnedComponent>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.supportAdmin,
    header: () => <ArrowTooltip trigger="Support...">Support Admin</ArrowTooltip>,
    cell: ({ getValue, row }) => {
      const session = getValue();
      const rowNumber = row.index + 1;
      return (
        <div className="flex justify-center">
          <CheckboxUpdateSupportAdmin
            {...session}
            checkboxTestId={adminTableRowTestId(tablePrefix, 'checkbox-support-admin', rowNumber)}
          />
        </div>
      );
    },
    size: 60,
  }),

  adminCol.accessor('videoConferenceType', {
    id: SessionPageColumns.videoConferenceType,
    header: () => <ArrowTooltip trigger="Meeting ty...">Meeting type</ArrowTooltip>,
    cell: ({ getValue, row }) => {
      const meetingType = getValue();
      const rowNumber = row.index + 1;
      if (!meetingType) {
        return <span data-testid={adminTableCellTestId(tablePrefix, 'meeting-type', rowNumber)}>—</span>;
      }
      return (
        <span data-testid={adminTableCellTestId(tablePrefix, 'meeting-type', rowNumber)}>
          {VideoConferenceTypeLabels[meetingType]}
        </span>
      );
    },
  }),

  adminCol.accessor((row) => row.zoom?.attendeeUrl, {
    id: SessionPageColumns.zoomLink,
    header: 'Zoom link',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const rowNumber = row.index + 1;
      return link ? (
        <SessionLink href={link} data-testid={adminTableRowTestId(tablePrefix, 'link-zoom', rowNumber)} />
      ) : (
        <span data-testid={adminTableCellTestId(tablePrefix, 'zoom-link', rowNumber)}>—</span>
      );
    },
  }),

  adminCol.accessor((row) => row.zoom?.hostUrl, {
    id: SessionPageColumns.zoomHostUrl,
    header: 'Zoom host URL',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const rowNumber = row.index + 1;
      if (!link) {
        return <span data-testid={adminTableCellTestId(tablePrefix, 'zoom-host-url', rowNumber)}>—</span>;
      }

      return (
        <span className="inline-flex items-center gap-2">
          <SessionLink href={link} data-testid={adminTableRowTestId(tablePrefix, 'link-zoom-host', rowNumber)}>
            Open Zoom host link
          </SessionLink>
          <CopyButton
            content={link}
            buttonProps={{
              'data-testid': adminTableRowTestId(tablePrefix, 'button-copy-zoom-host', rowNumber),
            }}
          />
        </span>
      );
    },
  }),

  adminCol.accessor((row) => row.coach?.coachProfile?.zoomPersonalMeetingId, {
    id: SessionPageColumns.zoomPersonalMeetingId,
    header: 'Zoom ID',
    cell: ({ getValue, row }) => {
      const rowNumber = row.index + 1;
      const value = getValue();
      return <span data-testid={adminTableCellTestId(tablePrefix, 'zoom-id', rowNumber)}>{value || '—'}</span>;
    },
  }),

  adminCol.accessor((row) => row.googleMeet?.attendeeUrl, {
    id: SessionPageColumns.googleMeetLink,
    header: 'Google Meet URL',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const rowNumber = row.index + 1;
      return link ? (
        <SessionLink href={link} data-testid={adminTableRowTestId(tablePrefix, 'link-google-meet', rowNumber)} />
      ) : (
        <span data-testid={adminTableCellTestId(tablePrefix, 'google-meet-url', rowNumber)}>—</span>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.googleMeetChecked,
    header: () => <ArrowTooltip trigger="Google M...">Google Meet checked</ArrowTooltip>,
    cell: ({ getValue, row }) => {
      const session = getValue();
      const rowNumber = row.index + 1;
      return (
        <div className="flex justify-center">
          <CheckboxGoogleMeetChecked
            {...session}
            checkboxTestId={adminTableRowTestId(tablePrefix, 'checkbox-google-meet', rowNumber)}
          />
        </div>
      );
    },
    size: 60,
  }),

  adminCol.accessor('lessonLink', {
    id: SessionPageColumns.lessonLink,
    header: 'Lesson',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const lesson = row.original.lesson;
      const rowNumber = row.index + 1;
      if (!lesson) {
        return <span data-testid={adminTableCellTestId(tablePrefix, 'lesson', rowNumber)}>—</span>;
      }
      if (!link) {
        return <span data-testid={adminTableCellTestId(tablePrefix, 'lesson', rowNumber)}>{lesson.name}</span>;
      }

      return (
        <CopyableLink
          externalHref={link}
          className="text-blue-600 hover:text-blue-800 underline"
          showExternalHrefIcon={false}
          data-testid={adminTableRowTestId(tablePrefix, 'link-lesson', rowNumber)}
        >
          {lesson.name}
        </CopyableLink>
      );
    },
  }),

  adminCol.accessor('coach', {
    id: SessionPageColumns.teacherAttendanceGrade,
    header: () => <ArrowTooltip trigger="Teach...">Teacher attendance</ArrowTooltip>,
    cell: ({ getValue, row }) => {
      const coach = getValue();
      const rowNumber = row.index + 1;
      return (
        <span data-testid={adminTableCellTestId(tablePrefix, 'teacher-attendance', rowNumber)}>
          {getCoachAttendanceGradeLabel(coach?.coachProfile?.attendanceGrade)}
        </span>
      );
    },
  }),

  adminCol.accessor('school', {
    id: 'school',
    header: 'School - District',
    cell: ({ getValue, row }) => {
      const school = getValue();
      const rowNumber = row.index + 1;
      if (!school) {
        return <span data-testid={adminTableCellTestId(tablePrefix, 'school', rowNumber)}>Unknown - Unknown</span>;
      }
      return (
        <Link
          to={'/orgs/schools/$schoolId/schedules'}
          params={{ schoolId: school.id }}
          className="text-blue-600"
          data-testid={adminTableRowTestId(tablePrefix, 'link-school', rowNumber)}
        >
          {school.name} - <span className="text-blue-600">{school.org.name}</span>
        </Link>
      );
    },
  }),

  adminCol.accessor('joinedStudentsCount', {
    id: SessionPageColumns.joinedStudentsCount,
    header: () => <ArrowTooltip trigger="#..."># of students joined</ArrowTooltip>,
    cell: ({ getValue, row }) => (
      <span data-testid={adminTableCellTestId(tablePrefix, 'students-joined-count', row.index + 1)}>{getValue()}</span>
    ),
  }),

  adminCol.accessor('students', {
    id: 'students',
    header: 'Students',
    cell: ({ getValue, row }) => {
      return (
        <PeopleBadgeList
          people={getValue()}
          horizontal
          testId={adminTableCellTestId(tablePrefix, 'students', row.index + 1)}
        />
      );
    },
  }),

  adminCol.accessor('gradeByStudentId', {
    id: 'grades',
    header: 'Grades',
    cell: ({ getValue, row }) => {
      const grades = Object.values(getValue()).filter(distinct()).filter(isNotNullish);
      const rowNumber = row.index + 1;
      if (!grades || grades.length === 0) {
        return <span data-testid={adminTableCellTestId(tablePrefix, 'grades', rowNumber)}>-</span>;
      }

      return (
        <>
          {grades.map((grade, index) => {
            if (!grade) return null;
            const backgroundColor = StudentYearColorMap[grade];
            return (
              <span
                key={index}
                className="text-xs rounded-full px-2 py-1 min-w-9 text-center mr-1"
                style={{ backgroundColor }}
                data-testid={adminTableCellTestId(tablePrefix, 'grades', rowNumber)}
              >
                {studentYearOption(grade)}
              </span>
            );
          })}
        </>
      );
    },
  }),

  adminCol.accessor('coach', {
    id: SessionPageColumns.originalTeacher,
    header: 'Original teacher',
    cell: ({ getValue, row }) => {
      const coach = getValue();
      const rowNumber = row.index + 1;
      if (!coach) return null;
      return (
        <PeopleBadgeList people={[coach]} testId={adminTableCellTestId(tablePrefix, 'original-teacher', rowNumber)} />
      );
    },
  }),

  adminCol.accessor('coachSessionRemarksV2', {
    id: SessionPageColumns.coachSessionRemarksV2,
    header: () => <CoachRemarksV2Header />,
    cell: ({ getValue, row }) => (
      <TeacherDisplayTagBadgeV2
        tags={getValue()}
        testId={adminTableCellTestId(tablePrefix, 'session-remarks', row.index + 1)}
      />
    ),
  }),

  adminCol.accessor('originalTeacherIssue', {
    id: SessionPageColumns.originalTeacherIssue,
    header: () => <ArrowTooltip trigger="Original...">Original teacher issue</ArrowTooltip>,
    cell: ({ getValue, row }) => {
      const originalTeacherIssue = getValue();
      const rowNumber = row.index + 1;
      if (!originalTeacherIssue) {
        return null;
      }
      return (
        <span data-testid={adminTableCellTestId(tablePrefix, 'original-teacher-issue', rowNumber)}>
          {OriginalTeacherIssueLabelMap[originalTeacherIssue]}
        </span>
      );
    },
  }),

  adminCol.accessor('canceledAtInstant', {
    id: SessionPageColumns.canceledAtInstant,
    header: () => <ArrowTooltip trigger="Cancelation...">Cancelation timestamp</ArrowTooltip>,
    cell: ({ getValue, row }) => {
      const canceledAtInstant = getValue();
      const rowNumber = row.index + 1;

      if (!canceledAtInstant) return null;

      const { formattedTime, formattedDate } = formatInstant(canceledAtInstant, displayTimeZone, {
        hour12: true,
      });

      return (
        <span className="lowercase" data-testid={adminTableCellTestId(tablePrefix, 'cancelation-timestamp', rowNumber)}>
          {`${formattedTime}, ${formattedDate}`}
        </span>
      );
    },
  }),

  sessionAirIdTableColumn((rowNumber) => adminTableCellTestId(tablePrefix, 'session-id', rowNumber)),
];

export const pastSessionsPageColumns = (displayTimeZone: string) => [
  createTableRowSelectionCell(adminCol),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.editSession,
    header: '',
    cell: ({ getValue }) => <EditSessionModal session={getValue()} />,
    size: 50,
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.date,
    header: 'Date (CT)',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: row.original.id }} search={{ from: 'past' }}>
          {formatDateWithDayOfWeek(startInstant, displayTimeZone)}
        </Link>
      );
    },
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.time,
    header: 'Time (CT)',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      const endInstant = row.original.endInstant;
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: row.original.id }} search={{ from: 'past' }}>
          {formatTimeRange(startInstant, endInstant, FrequentTimeZones.UsCentral)}
        </Link>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionTeacher,
    header: 'Session teacher',
    cell: ({ getValue }) => {
      return <SessionTeacherDisplay session={getValue()} />;
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionCompletionStatus,
    header: 'Teacher display - class status',
    cell: ({ getValue }) => {
      const {
        sessionCompletionStatus,
        coachSessionRemarksV2,
        zoom,
        startInstant,
        endInstant,
        addedCoachSessionRemarksV2,
      } = getValue();
      return (
        <SessionCompletionStatusCell
          status={sessionCompletionStatus}
          coachSessionRemarksV2={coachSessionRemarksV2}
          session={{ zoom, startInstant, endInstant, addedCoachSessionRemarksV2 }}
        />
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: 'feedback',
    header: 'Feedback',
    cell: ({ getValue }) => {
      const session = getValue();
      const { hoursCanceledBeforeStart, sessionFeedbacks, lesson } = session;

      return (
        <SessionFeedbackStatus
          feedbacks={sessionFeedbacks || []}
          hoursCanceledBeforeStart={hoursCanceledBeforeStart}
          lessonShouldSkipFeedback={lesson.shouldSkipFeedback}
        />
      );
    },
  }),

  adminCol.accessor('students', {
    id: 'students',
    header: 'Students',
    cell: ({ getValue }) => {
      return <PeopleBadgeList people={getValue()} />;
    },
  }),

  adminCol.accessor('gradeByStudentId', {
    id: 'grades',
    header: 'Grade',
    cell: ({ getValue }) => {
      const grades = Object.values(getValue()).filter(distinct()).filter(isNotNullish);
      if (!grades || grades.length === 0) return <div>-</div>;

      return (
        <div className="flex gap-1 flex-wrap">
          {grades.map((grade, index) => {
            if (!grade) return null;
            const backgroundColor = StudentYearColorMap[grade];
            return (
              <span
                key={index}
                className="text-xs rounded-full px-2 py-1 min-w-9 text-center"
                style={{ backgroundColor }}
              >
                {studentYearOption(grade)}
              </span>
            );
          })}
        </div>
      );
    },
  }),

  adminCol.accessor('school', {
    id: 'school',
    header: 'School - District',
    cell: ({ getValue }) => {
      const school = getValue();
      if (!school) return <span>Unknown - Unknown</span>;
      return (
        <>
          <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
            {school.name} - <span className="text-blue-600">{school.org.name}</span>
          </Link>
        </>
      );
    },
  }),

  adminCol.accessor('lessonLink', {
    id: SessionPageColumns.lessonLink,
    header: 'Lesson',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const lesson = row.original.lesson;
      if (!lesson) return '—';
      if (!link) return <div className="flex items-center gap-2">{lesson.name}</div>;

      return (
        <div className="flex items-center gap-2">
          <CopyableLink
            externalHref={link}
            className="text-blue-600 hover:text-blue-800 underline"
            showExternalHrefIcon={false}
          >
            {lesson.name}
          </CopyableLink>
        </div>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.recordingLink,
    header: 'Recording',
    cell: ({ getValue }) => {
      const session = getValue();
      const recordingUrls = session.zoom?.recordingUrls ?? [];
      if (recordingUrls.length === 0) {
        return <div>-</div>;
      }

      return (
        <div className="flex flex-col gap-1">
          {recordingUrls.map((url, index) => (
            <LinkToExternal key={`${url}-${index}`} href={url} className="text-blue-600 hover:text-blue-800 underline">
              {recordingUrls.length > 1 ? `Recording ${index + 1}` : 'View Recording'}
            </LinkToExternal>
          ))}
        </div>
      );
    },
  }),

  adminCol.accessor('coach', {
    id: SessionPageColumns.originalTeacher,
    header: 'Original teacher',
    cell: ({ getValue }) => {
      const coach = getValue();
      return coach ? <PeopleBadgeList people={[coach]} /> : null;
    },
  }),

  adminCol.accessor('coachSessionRemarksV2', {
    id: SessionPageColumns.coachSessionRemarksV2,
    header: () => <CoachRemarksV2Header />,
    cell: ({ getValue }) => <TeacherDisplayTagBadgeV2 tags={getValue()} />,
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.substituteTimestamp,
    header: 'Substitute timestamp',
    cell: ({ getValue }) => {
      const session = getValue();
      const substitutionRequest = session.substitutionRequests[0];
      if (!substitutionRequest?.acceptedAtInstant) return null;

      const { formattedTime, formattedDate } = formatInstant(substitutionRequest.acceptedAtInstant, displayTimeZone, {
        hour12: true,
      });

      return <span className="lowercase">{`${formattedTime}, ${formattedDate}`}</span>;
    },
  }),

  adminCol.accessor('canceledAtInstant', {
    id: SessionPageColumns.canceledAtInstant,
    header: 'Cancelation timestamp',
    cell: ({ getValue }) => {
      const canceledAtInstant = getValue();

      if (!canceledAtInstant) return null;

      const { formattedTime, formattedDate } = formatInstant(canceledAtInstant, displayTimeZone, {
        hour12: true,
      });

      return <span className="lowercase">{`${formattedTime}, ${formattedDate}`}</span>;
    },
  }),

  sessionAirIdTableColumn(),
];

export const subSessionsPageColumns = (displayTimeZone: string, from?: string) => [
  createTableRowSelectionCell(adminCol),

  adminCol.accessor('id', {
    id: SessionPageColumns.sessionId,
    header: '',
    cell: ({ getValue }) => {
      const searchProps = from ? { search: { from } } : {};
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: getValue() }} {...searchProps}>
          <ViewIcon size={16} />
        </Link>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.subSessionDrawer,
    header: '',
    cell: ({ getValue }) => {
      const session = getValue();
      return (
        <SubSessionDrawer
          studentGroupId={session.studentGroupId}
          session={session}
          trigger={
            <Button variant="ghost" size="icon">
              <UserPenIcon size={18} />
            </Button>
          }
        />
      );
    },
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.date,
    header: 'Date',
    cell: ({ getValue }) => {
      const startInstant = getValue();
      return formatDateWithDayOfWeek(startInstant, displayTimeZone);
    },
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.time,
    header: 'Time (CT)',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      const endInstant = row.original.endInstant;
      return formatTimeRange(startInstant, endInstant, FrequentTimeZones.UsCentral);
    },
  }),

  adminCol.accessor('subNotes', {
    id: 'subNotes',
    header: 'Sub notes',
    maxSize: 250,
    cell: ({ getValue }) => {
      const subNotes = getValue();
      if (!subNotes) return '—';
      return (
        <div className="max-w-[250px] truncate" title={subNotes}>
          {subNotes}
        </div>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: 'pinnedSubSession',
    header: 'Hold session',
    cell: ({ getValue }) => <CheckboxPinnedSubSession {...getValue()} />,
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionTeacher,
    header: 'Session teacher',
    cell: ({ getValue }) => {
      return <SessionTeacherDisplay session={getValue()} />;
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.originalTeacher,
    header: 'Original teacher',
    cell: ({ row }) => {
      return <OriginalTeacherDisplay session={row.original} />;
    },
  }),

  adminCol.accessor('videoConferenceType', {
    id: SessionPageColumns.videoConferenceType,
    header: 'Meeting type',
    cell: ({ getValue }) => {
      const meetingType = getValue();
      if (!meetingType) return '—';
      return VideoConferenceTypeLabels[meetingType];
    },
  }),

  adminCol.accessor((row) => row.coach?.coachProfile?.zoomPersonalMeetingId, {
    id: SessionPageColumns.zoomPersonalMeetingId,
    header: 'Zoom ID',
    cell: ({ getValue }) => getValue() || '—',
  }),

  adminCol.accessor((row) => row.zoom?.attendeeUrl, {
    id: SessionPageColumns.zoomLink,
    header: 'Zoom link',
    cell: ({ getValue }) => {
      const link = getValue();
      return link ? <SessionLink href={link} /> : '—';
    },
  }),

  adminCol.accessor((row) => row.zoom?.hostUrl, {
    id: SessionPageColumns.zoomHostUrl,
    header: 'Zoom host URL',
    cell: ({ getValue }) => {
      const link = getValue();
      if (!link) return '—';

      return (
        <div className="flex items-center gap-2">
          <SessionLink href={link}>Open Zoom host link</SessionLink>
          <CopyButton content={link} />
        </div>
      );
    },
  }),

  adminCol.accessor((row) => row.googleMeet?.attendeeUrl, {
    id: SessionPageColumns.googleMeetLink,
    header: 'Google Meet URL',
    cell: ({ getValue }) => {
      const link = getValue();
      return link ? <SessionLink href={link} /> : '—';
    },
  }),

  adminCol.accessor('lessonLink', {
    id: SessionPageColumns.lessonLink,
    header: 'Lesson',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const lesson = row.original.lesson;
      if (!lesson) return '—';
      if (!link) return <div className="flex items-center gap-2">{lesson.name}</div>;

      return (
        <div className="flex items-center gap-2">
          <CopyableLink
            externalHref={link}
            className="text-blue-600 hover:text-blue-800 underline"
            showExternalHrefIcon={false}
          >
            {lesson.name}
          </CopyableLink>
        </div>
      );
    },
  }),

  adminCol.accessor('school', {
    id: 'school',
    header: 'School',
    cell: ({ getValue }) => {
      const school = getValue();
      if (!school) return <span>Unknown</span>;
      return (
        <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
          {school.name}
        </Link>
      );
    },
  }),

  adminCol.accessor('gradeByStudentId', {
    id: 'grades',
    header: 'Grades',
    cell: ({ getValue }) => {
      const grades = Object.values(getValue()).filter(distinct()).filter(isNotNullish);
      if (!grades || grades.length === 0) return <div>-</div>;

      return (
        <div className="flex gap-1 flex-wrap">
          {grades.map((grade, index) => {
            if (!grade) return null;
            const backgroundColor = StudentYearColorMap[grade];
            return (
              <span
                key={index}
                className="text-xs rounded-full px-2 py-1 min-w-9 text-center"
                style={{ backgroundColor }}
              >
                {studentYearOption(grade)}
              </span>
            );
          })}
        </div>
      );
    },
  }),

  adminCol.accessor('students', {
    id: 'students',
    header: 'Students',
    cell: ({ getValue }) => {
      return <PeopleBadgeList people={getValue()} />;
    },
  }),

  adminCol.accessor((row) => row.students?.length ?? 0, {
    id: SessionPageColumns.numberOfStudents,
    header: '# of students',
    cell: ({ getValue }) => getValue(),
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.substituteTimestamp,
    header: 'Substitute timestamp',
    cell: ({ getValue }) => {
      const session = getValue();
      const { firstNonCanceledOriginalTeacherRequest } = getSessionSubstitutionRequestSummary(session);
      if (!firstNonCanceledOriginalTeacherRequest?.createdAtInstant) return '-';

      const { formattedTime, formattedDate } = formatInstant(
        firstNonCanceledOriginalTeacherRequest.createdAtInstant,
        displayTimeZone,
        {
          hour12: true,
        },
        ', ',
      );

      return <span className="lowercase">{`${formattedDate}, ${formattedTime}`}</span>;
    },
  }),

  sessionAirIdTableColumn(),
];

export const overlappingSessionsPageColumns = (displayTimeZone: string) => [
  createTableRowSelectionCell(adminCol),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.editSession,
    header: '',
    cell: ({ getValue }) => <EditSessionModal session={getValue()} />,
    size: 50,
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.date,
    header: 'Date',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: row.original.id }} search={{ from: 'past' }}>
          {formatDateWithDayOfWeek(startInstant, displayTimeZone)}
        </Link>
      );
    },
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.time,
    header: 'Time (CT)',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      const endInstant = row.original.endInstant;
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: row.original.id }} search={{ from: 'past' }}>
          {formatTimeRange(startInstant, endInstant, FrequentTimeZones.UsCentral)}
        </Link>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionTeacher,
    header: 'Session teacher',
    cell: ({ getValue }) => {
      return <SessionTeacherDisplay session={getValue()} />;
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.originalTeacher,
    header: 'Original teacher',
    cell: ({ row }) => {
      return <OriginalTeacherDisplay session={row.original} />;
    },
  }),

  adminCol.accessor('school', {
    id: 'district',
    header: 'District',
    cell: ({ getValue }) => {
      const school = getValue();
      const districtName = school?.org?.name;
      if (!districtName) return <span>Unknown</span>;
      return getDistrictColumn({
        orgName: districtName,
        shouldHaveLink: false,
      });
    },
  }),

  adminCol.accessor('school', {
    id: 'school',
    header: 'School',
    cell: ({ getValue }) => {
      const school = getValue();
      if (!school) return <span>Unknown</span>;
      return (
        <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
          {school.name}
        </Link>
      );
    },
  }),

  adminCol.accessor('students', {
    id: 'students',
    header: 'Students',
    cell: ({ getValue }) => {
      return <PeopleBadgeList people={getValue()} />;
    },
  }),

  adminCol.accessor('coachSessionRemarksV2', {
    id: SessionPageColumns.coachSessionRemarksV2,
    header: () => <CoachRemarksV2Header />,
    cell: ({ getValue }) => <TeacherDisplayTagBadgeV2 tags={getValue()} />,
  }),

  sessionAirIdTableColumn(),
];

export const teacherFutureSessionsPageColumns = (displayTimeZone: string) => [
  createTableRowSelectionCell(adminCol),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.date,
    header: 'Date',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: row.original.id }} search={{ from: 'teacher-future' }}>
          {formatDateWithDayOfWeek(startInstant, displayTimeZone)}
        </Link>
      );
    },
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.time,
    header: 'Time (CT)',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      const endInstant = row.original.endInstant;
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: row.original.id }} search={{ from: 'teacher-future' }}>
          {formatTimeRange(startInstant, endInstant, displayTimeZone)}
        </Link>
      );
    },
  }),

  adminCol.accessor('students', {
    id: SessionPageColumns.students,
    header: 'Students',
    cell: ({ getValue }) => {
      const students = getValue();
      return <PeopleBadgeList people={students} wrap={true} />;
    },
  }),

  adminCol.accessor('gradeByStudentId', {
    id: SessionPageColumns.grades,
    header: 'Grade',
    cell: ({ getValue }) => {
      const grades = Object.values(getValue()).filter(distinct()).filter(isNotNullish);
      if (!grades || grades.length === 0) return <div>-</div>;

      return (
        <div className="flex gap-1 flex-wrap">
          {grades.map((grade, index) => {
            if (!grade) return null;
            const backgroundColor = StudentYearColorMap[grade];
            return (
              <span
                key={index}
                className="text-xs rounded-full px-2 py-1 min-w-9 text-center"
                style={{ backgroundColor }}
              >
                {studentYearOption(grade)}
              </span>
            );
          })}
        </div>
      );
    },
  }),

  adminCol.accessor('school', {
    id: SessionPageColumns.school,
    header: 'School',
    cell: ({ getValue }) => {
      const school = getValue();
      return school?.name || 'Unknown';
    },
  }),

  adminCol.accessor('school', {
    id: SessionPageColumns.district,
    header: 'District',
    cell: ({ getValue }) => {
      const school = getValue();
      return school?.org?.name || 'Unknown';
    },
  }),

  adminCol.accessor('lessonLink', {
    id: SessionPageColumns.lessonName,
    header: 'Lesson',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const lesson = row.original.lesson;
      if (!lesson) return '—';
      if (!link) return <div className="flex items-center gap-2">{lesson.name}</div>;

      return (
        <CopyableLink
          externalHref={link}
          className="text-blue-600 hover:text-blue-800 underline"
          showExternalHrefIcon={false}
        >
          {lesson.name}
        </CopyableLink>
      );
    },
  }),

  adminCol.accessor('coachSessionRemarksV2', {
    id: SessionPageColumns.coachSessionRemarksV2,
    header: () => <CoachRemarksV2Header />,
    cell: ({ getValue }) => <TeacherDisplayTagBadgeV2 tags={getValue()} />,
  }),

  sessionAirIdTableColumn(),
];

export const teacherPastSessionsPageColumns = (displayTimeZone: string) => [
  createTableRowSelectionCell(adminCol),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.editSession,
    header: '',
    cell: ({ getValue }) => <EditSessionModal session={getValue()} />,
    size: 50,
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.date,
    header: 'Date',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: row.original.id }} search={{ from: 'teacher-past' }}>
          {formatDateWithDayOfWeek(startInstant, displayTimeZone)}
        </Link>
      );
    },
  }),

  adminCol.accessor('startInstant', {
    id: SessionPageColumns.time,
    header: 'Time (CT)',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      const endInstant = row.original.endInstant;
      return (
        <Link to="/sessions/$sessionId" params={{ sessionId: row.original.id }} search={{ from: 'teacher-past' }}>
          {formatTimeRange(startInstant, endInstant, displayTimeZone)}
        </Link>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.sessionCompletionStatus,
    header: 'Completion status',
    cell: ({ getValue }) => {
      const {
        sessionCompletionStatus,
        coachSessionRemarksV2,
        zoom,
        startInstant,
        endInstant,
        addedCoachSessionRemarksV2,
      } = getValue();
      return (
        <SessionCompletionStatusCell
          status={sessionCompletionStatus}
          coachSessionRemarksV2={coachSessionRemarksV2}
          session={{ zoom, startInstant, endInstant, addedCoachSessionRemarksV2 }}
        />
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: 'feedback',
    header: 'Feedback status',
    cell: ({ getValue }) => {
      const session = getValue();
      const { hoursCanceledBeforeStart, sessionFeedbacks, lesson } = session;

      return (
        <SessionFeedbackStatus
          feedbacks={sessionFeedbacks || []}
          hoursCanceledBeforeStart={hoursCanceledBeforeStart}
          lessonShouldSkipFeedback={lesson.shouldSkipFeedback}
        />
      );
    },
  }),

  adminCol.accessor('students', {
    id: SessionPageColumns.students,
    header: 'Students',
    cell: ({ getValue }) => {
      const students = getValue();
      return <PeopleBadgeList people={students} wrap={true} />;
    },
  }),

  adminCol.accessor('gradeByStudentId', {
    id: SessionPageColumns.grades,
    header: 'Grade',
    cell: ({ getValue }) => {
      const grades = Object.values(getValue()).filter(distinct()).filter(isNotNullish);
      if (!grades || grades.length === 0) return <div>-</div>;

      return (
        <div className="flex gap-1 flex-wrap">
          {grades.map((grade, index) => {
            if (!grade) return null;
            const backgroundColor = StudentYearColorMap[grade];
            return (
              <span
                key={index}
                className="text-xs rounded-full px-2 py-1 min-w-9 text-center"
                style={{ backgroundColor }}
              >
                {studentYearOption(grade)}
              </span>
            );
          })}
        </div>
      );
    },
  }),

  adminCol.accessor('school', {
    id: SessionPageColumns.school,
    header: 'School - District',
    cell: ({ getValue }) => {
      const school = getValue();
      return (
        <>
          {school?.name || 'Unknown'} - {school?.org.name || 'Unknown'}
        </>
      );
    },
  }),

  adminCol.accessor('lessonLink', {
    id: 'lessonName',
    header: 'Lesson',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const lesson = row.original.lesson;
      if (!link || !lesson) return '—';

      return (
        <div className="flex items-center gap-2">
          <Link to={'/lessons/$lessonId'} params={{ lessonId: lesson.id }} className="underline">
            {lesson.name}
          </Link>
        </div>
      );
    },
  }),

  adminCol.accessor((row) => row, {
    id: SessionPageColumns.recordingLink,
    header: 'Recording',
    cell: ({ getValue }) => {
      const session = getValue();
      const recordingUrls = session.zoom?.recordingUrls ?? [];
      if (recordingUrls.length === 0) {
        return <div>-</div>;
      }

      return (
        <div className="flex flex-col gap-1">
          {recordingUrls.map((url, index) => (
            <LinkToExternal key={`${url}-${index}`} href={url} className="text-blue-600 hover:text-blue-800 underline">
              {recordingUrls.length > 1 ? `Recording ${index + 1}` : 'View Recording'}
            </LinkToExternal>
          ))}
        </div>
      );
    },
  }),

  adminCol.accessor('coachSessionRemarksV2', {
    id: SessionPageColumns.coachSessionRemarksV2,
    header: () => <CoachRemarksV2Header />,
    cell: ({ getValue }) => <TeacherDisplayTagBadgeV2 tags={getValue()} />,
  }),

  sessionAirIdTableColumn(),
];
